import * as React from 'react';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import {
  InstantSearch,
  SearchBox,
  Configure,
  Pagination,
  useHits,
} from 'react-instantsearch';
import Typography from '@mui/material/Typography';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { Filter, LoadedUser } from '../interfaces';
import { API_KEY, APP_ID, INDEX } from '../algoliaconfig';
import MultiSelectFilter from '../components/filtering/MultiSelectFilter';
import { getCleanedPhone, important } from '../utils/global/helper';
import 'instantsearch.css/themes/satellite.css';
import TableTitle from '../components/table/TableTitle';
import FullPageModal from '../components/modal/FullPageModal';
import CustomerForm from '../components/form/CustomerForm';
import useUser from '../hooks/global/useUser';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import PageLayout from '../components/global/PageLayout';
import CustomerSideBarModal from '../components/customers/CustomerSideBarModal';
import { CustomerOverview } from '../types/customers';
import {
  getInitialArtistOptions,
  getInitialSelectedArtists,
} from '../helpers/employees';
import { FILTER_PERSISTENCE_KEYS } from '../constants/global';

const ARTIST_FILTER_NAME = FILTER_PERSISTENCE_KEYS.customerArtists;

function HitComponent(props: any) {
  const { openCustomerModal } = props;
  const { hits, results } = useHits(props);

  return (
    <div className="table">
      <TableTitle
        name="Customers"
        showing={results?.hits.length || 0}
        total={results?.nbHits || 0}
      />
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell component="th" scope="header">
                Name
              </TableCell>
              <TableCell component="th" scope="header">
                Phone Number
              </TableCell>
              <TableCell component="th" scope="header">
                Email
              </TableCell>
              <TableCell component="th" scope="header">
                View Details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hits.map(hit => (
              <TableRow key={hit.customerId as string}>
                <TableCell>
                  <Typography variant="body1">
                    {hit.fullName as string}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    {getCleanedPhone(hit.phoneNumber?.toString() ?? '')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">{hit.email as string}</Typography>
                </TableCell>
                <TableCell>
                  <Button
                    onClick={() => openCustomerModal(hit.customerId)}
                    variant="outlined"
                    className="table-button">
                    View Customer
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination />
    </div>
  );
}

const searchClient = algoliasearch(APP_ID, API_KEY);

export default function CustomersPage() {
  const { customerId } = useParams();
  const { user: loadedUser } = useUser<LoadedUser>();

  const [loading, setLoading] = React.useState(true);

  const [newCustomerModalOpen, setNewCustomerModalOpen] = React.useState(false);
  const [shouldScrollToTop, setShouldScrollToTop] = React.useState(false);

  const [openedCustomerId, setOpenedCustomerId] = React.useState<string | null>(
    null,
  );
  const [selectedCustomer, setSelectedCustomer] =
    React.useState<CustomerOverview>();

  const artistOptions = React.useMemo(() => {
    if (!loadedUser) return [];
    return getInitialArtistOptions(loadedUser);
  }, [loadedUser]);

  const [selectedArtists, setSelectedArtists] = React.useState(() =>
    getInitialSelectedArtists(loadedUser, artistOptions, ARTIST_FILTER_NAME),
  );
  const artistFilter = React.useMemo<Filter>(
    () => ({
      name: ARTIST_FILTER_NAME,
      options: artistOptions,
      state: selectedArtists,
      setState: setSelectedArtists,
      emptyLabel: 'Select an artist',
      allLabel: 'All artists',
    }),
    [artistOptions, selectedArtists],
  );

  const [algoliaFilters, setAlgoliaFilters] = React.useState('');

  const { openSubscriptionModal } = useSubscriptionModal();
  const canManageCustomers = useFeatureGuard(Feature.MANAGE_CUSTOMER);
  const canAddCustomers = useFeatureGuard(Feature.ADD_CUSTOMER);

  // Set up click action for each row of the customers table
  const openCustomerModal = React.useCallback(
    async (tempCustomerId: string) => {
      if (!loadedUser?.business.id) return;
      if (!canManageCustomers) {
        openSubscriptionModal();
        return;
      }

      setOpenedCustomerId(tempCustomerId);
    },
    [canManageCustomers, loadedUser, openSubscriptionModal],
  );

  React.useEffect(() => {
    if (customerId) {
      openCustomerModal(customerId);
    }
  }, [customerId, openCustomerModal]);

  // Set up click action for edit customer form
  const openEditCustomer = React.useCallback(
    (customer: CustomerOverview) => {
      if (!canManageCustomers) return;
      setSelectedCustomer(customer);
      setNewCustomerModalOpen(true);
    },
    [canManageCustomers],
  );

  // Close edit appointment
  const closeEditCustomer = React.useCallback(() => {
    setSelectedCustomer(undefined);
    setNewCustomerModalOpen(false);
  }, []);

  React.useEffect(() => {
    const shopFilterString = `businessId:${loadedUser?.business.id}`;
    const artistFilterString = `employeeId:${loadedUser?.userId}`;

    if (!loadedUser?.canViewAllShopCustomers) {
      setAlgoliaFilters(artistFilterString);
      return;
    }

    let otherArtistsString = '';
    if (
      selectedArtists &&
      selectedArtists.length > 0 &&
      selectedArtists[0] !== 'Initialize' &&
      selectedArtists.length !== artistFilter?.options.length
    ) {
      otherArtistsString += '(';
      selectedArtists.forEach((artist, index) => {
        otherArtistsString += `employeeId:${artist}`;
        if (selectedArtists[index + 1]) {
          otherArtistsString += ` OR `;
        } else {
          otherArtistsString += ')';
        }
      });
    }

    if (otherArtistsString) {
      setAlgoliaFilters(`${shopFilterString} AND ${otherArtistsString}`);
    } else {
      setAlgoliaFilters(`${shopFilterString} OR ${artistFilterString}`);
    }
  }, [
    artistFilter?.options.length,
    loadedUser?.business.id,
    loadedUser.role,
    loadedUser?.userId,
    selectedArtists,
    loading,
    loadedUser?.canViewAllShopCustomers,
  ]);

  const openCustomerForm = () => {
    if (!canAddCustomers) {
      openSubscriptionModal();
      return;
    }
    setNewCustomerModalOpen(true);
  };

  return (
    <InstantSearch searchClient={searchClient} indexName={INDEX} insights>
      <Configure filters={algoliaFilters} distinct />
      <PageLayout title="Customers">
        <Box
          className="header-right"
          sx={{
            display: {
              xs: 'inline',
              md: 'none',
            },
          }}>
          <Button
            variant="contained"
            className="primary"
            onClick={openCustomerForm}>
            Add New
          </Button>
        </Box>
        <div className="customers-header">
          <div className="header-left">
            <SearchBox placeholder="Search customers" searchAsYouType />
            {artistFilter && (
              <MultiSelectFilter
                name={artistFilter.name}
                options={artistFilter.options}
                state={artistFilter.state}
                setState={artistFilter.setState}
                emptyLabel={artistFilter.emptyLabel}
                allLabel={artistFilter.allLabel}
              />
            )}
          </div>
          <Box
            className="header-right"
            sx={{
              display: {
                xs: important('none'),
                md: important('inline'),
              },
            }}>
            <Button
              variant="contained"
              className="primary"
              onClick={openCustomerForm}>
              Add New
            </Button>
          </Box>
        </div>
        <HitComponent openCustomerModal={openCustomerModal} />
      </PageLayout>

      <CustomerSideBarModal
        customerId={openedCustomerId}
        isOpen={!!openedCustomerId}
        onClose={() => setOpenedCustomerId(null)}
        onEditCustomer={openEditCustomer}
      />

      <FullPageModal
        modalOpen={newCustomerModalOpen}
        setModalOpen={setNewCustomerModalOpen}
        handleClose={closeEditCustomer}
        shouldScrollTop={shouldScrollToTop}
        setShouldScrollToTop={setShouldScrollToTop}
        name={selectedCustomer ? 'Edit Customer' : 'Add Customer'}>
        <CustomerForm
          loadedUser={loadedUser}
          setModalOpen={setNewCustomerModalOpen}
          setLoading={setLoading}
          setShouldScrollToTop={setShouldScrollToTop}
          selectedCustomer={selectedCustomer}
        />
      </FullPageModal>
    </InstantSearch>
  );
}
